import { Helmet } from "react-helmet-async";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useLanguage } from "@/contexts/LanguageContext";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Star, Quote, ArrowRight, CheckCircle } from "lucide-react";

const Reviews = () => {
  const { language } = useLanguage();
  const isGerman = language === "de";

  const reviews = [
    {
      name: "Sarah M.",
      business: isGerman ? "Restaurant Berlin" : "Restaurant Owner, Berlin",
      rating: 5,
      text: isGerman
        ? "Fuel72 hat fantastische AI UGC Videos für mein Restaurant erstellt. In nur 72 Stunden hatten wir professionellen Content ohne selbst filmen zu müssen. Absolut empfehlenswert!"
        : "Fuel72 created amazing AI UGC videos for my restaurant. In just 72 hours we had professional content without filming ourselves. Absolutely recommended!",
      date: "2024-12-01",
      verified: true,
    },
    {
      name: "Mike T.",
      business: isGerman ? "Fitness Studio NYC" : "Gym Owner, New York",
      rating: 5,
      text: isGerman
        ? "Bester AI UGC Generator den ich benutzt habe. Fuel72 hat genau das geliefert was wir für unsere Social Media brauchten. UGC ohne Influencer-Stress."
        : "Best AI UGC generator I've used. Fuel72 delivered exactly what we needed for our social media. Hire UGC without the hassle of finding influencers.",
      date: "2024-11-15",
      verified: true,
    },
    {
      name: "Lisa K.",
      business: isGerman ? "Beauty Salon Toronto" : "Salon Owner, Toronto",
      rating: 5,
      text: isGerman
        ? "Endlich eine Möglichkeit UGC Videos zu erstellen ohne vor der Kamera zu stehen. Fuel72's AI UGC Service ist genau das was mein Business gebraucht hat."
        : "Finally found a way to create UGC videos without being on camera. Fuel72's AI UGC service is exactly what my business needed.",
      date: "2024-11-20",
      verified: true,
    },
    {
      name: "Thomas B.",
      business: isGerman ? "E-Commerce München" : "E-Commerce, Munich",
      rating: 5,
      text: isGerman
        ? "Wir haben mit Fuel72 unsere Ads komplett umgestellt. Die AI UGC Videos performen 3x besser als unsere alten Creatives. ROI ist unglaublich."
        : "We completely revamped our ads with Fuel72. The AI UGC videos perform 3x better than our old creatives. ROI is incredible.",
      date: "2024-12-05",
      verified: true,
    },
    {
      name: "Emma S.",
      business: isGerman ? "Mode Boutique Wien" : "Fashion Boutique, Vienna",
      rating: 5,
      text: isGerman
        ? "Schnell, professionell und genau das was wir gebraucht haben. Die Videos sehen aus wie von echten Influencern - nur viel günstiger und schneller."
        : "Fast, professional and exactly what we needed. The videos look like they're from real influencers - just much cheaper and faster.",
      date: "2024-11-28",
      verified: true,
    },
    {
      name: "David R.",
      business: isGerman ? "SaaS Startup Zürich" : "SaaS Startup, Zurich",
      rating: 5,
      text: isGerman
        ? "Als Startup haben wir nicht das Budget für teure Creator. Fuel72 war die perfekte Lösung - professionelle UGC Videos zum Festpreis."
        : "As a startup we don't have the budget for expensive creators. Fuel72 was the perfect solution - professional UGC videos at a fixed price.",
      date: "2024-12-10",
      verified: true,
    },
    {
      name: "Anna P.",
      business: isGerman ? "Yoga Studio Hamburg" : "Yoga Studio, Hamburg",
      rating: 5,
      text: isGerman
        ? "Die Qualität hat mich überrascht. Authentisch, professionell und perfekt für Instagram und TikTok. Werde definitiv wieder bestellen."
        : "The quality surprised me. Authentic, professional and perfect for Instagram and TikTok. Will definitely order again.",
      date: "2024-11-22",
      verified: true,
    },
    {
      name: "James W.",
      business: isGerman ? "Immobilien Sydney" : "Real Estate, Sydney",
      rating: 5,
      text: isGerman
        ? "Fuel72 hat unsere Property Listings auf ein neues Level gehoben. Die AI Videos zeigen unsere Immobilien perfekt in Social Media Ads."
        : "Fuel72 elevated our property listings to a new level. The AI videos showcase our properties perfectly in social media ads.",
      date: "2024-12-08",
      verified: true,
    },
  ];

  const stats = [
    { value: "4.9", label: isGerman ? "Durchschnittliche Bewertung" : "Average Rating" },
    { value: "250+", label: isGerman ? "Zufriedene Kunden" : "Happy Customers" },
    { value: "98%", label: isGerman ? "Würden empfehlen" : "Would Recommend" },
    { value: "72h", label: isGerman ? "Ø Lieferzeit" : "Avg Delivery" },
  ];

  const reviewSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "Fuel72 AI UGC Video Service",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": reviews.length.toString(),
      "bestRating": "5",
      "worstRating": "1",
    },
    "review": reviews.map((r) => ({
      "@type": "Review",
      "author": { "@type": "Person", "name": r.name },
      "reviewBody": r.text,
      "reviewRating": {
        "@type": "Rating",
        "ratingValue": r.rating.toString(),
        "bestRating": "5",
      },
      "datePublished": r.date,
    })),
  };

  return (
    <>
      <Helmet>
        <title>
          {isGerman
            ? "Kundenbewertungen | Fuel72 Erfahrungen | 4.9★ | AI UGC Reviews"
            : "Customer Reviews | Fuel72 Testimonials | 4.9★ | AI UGC Reviews"}
        </title>
        <meta
          name="description"
          content={
            isGerman
              ? "Fuel72 Erfahrungen: 4.9★ von 250+ Kunden. Lesen Sie echte Bewertungen von Restaurants, Fitness Studios, E-Commerce und mehr. AI UGC das funktioniert."
              : "Fuel72 Reviews: 4.9★ from 250+ customers. Read real testimonials from restaurants, gyms, e-commerce and more. AI UGC that works."
          }
        />
        <meta
          name="keywords"
          content={
            isGerman
              ? "fuel72 erfahrungen, fuel72 bewertungen, fuel72 reviews, ai ugc erfahrungen, ugc bewertungen, fuel72 test"
              : "fuel72 reviews, fuel72 testimonials, fuel72 feedback, ai ugc reviews, ugc testimonials, fuel72 rating"
          }
        />
        <link rel="canonical" href="https://www.fuel72.com/reviews" />
        <link rel="alternate" hrefLang="en" href="https://www.fuel72.com/reviews" />
        <link rel="alternate" hrefLang="de" href="https://www.fuel72.com/reviews?lang=de" />
        <meta property="og:title" content={isGerman ? "Fuel72 Erfahrungen | 4.9★" : "Fuel72 Reviews | 4.9★"} />
        <meta property="og:url" content="https://www.fuel72.com/reviews" />
        <script type="application/ld+json">{JSON.stringify(reviewSchema)}</script>
      </Helmet>

      <div className="min-h-screen gradient-mesh">
        <Navbar />

        <main className="container mx-auto px-4 py-20 md:py-28">
          {/* Header */}
          <div className="text-center mb-16">
            <div className="flex justify-center gap-1 mb-4">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star key={star} className="w-8 h-8 fill-amber text-amber" />
              ))}
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold mb-6">
              {isGerman ? (
                <>
                  Was unsere{" "}
                  <span className="bg-gradient-to-r from-neon-purple via-neon-pink to-orange-500 bg-clip-text text-transparent">
                    Kunden sagen
                  </span>
                </>
              ) : (
                <>
                  What Our{" "}
                  <span className="bg-gradient-to-r from-neon-purple via-neon-pink to-orange-500 bg-clip-text text-transparent">
                    Customers Say
                  </span>
                </>
              )}
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              {isGerman
                ? "Echte Bewertungen von echten Unternehmen. Sehen Sie warum 250+ Kunden Fuel72 vertrauen."
                : "Real reviews from real businesses. See why 250+ customers trust Fuel72."}
            </p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto mb-16">
            {stats.map((stat, index) => (
              <div
                key={index}
                className="glass-morphism border border-white/10 rounded-xl p-6 text-center"
              >
                <div className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-neon-purple to-neon-pink bg-clip-text text-transparent mb-2">
                  {stat.value}
                </div>
                <div className="text-sm text-muted-foreground">{stat.label}</div>
              </div>
            ))}
          </div>

          {/* Reviews Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto mb-16">
            {reviews.map((review, index) => (
              <div
                key={index}
                className="glass-morphism border border-white/10 rounded-2xl p-6 hover:border-neon-purple/30 transition-colors"
              >
                <div className="flex items-start gap-4 mb-4">
                  <Quote className="w-8 h-8 text-neon-purple/30 flex-shrink-0" />
                  <div className="flex-1">
                    <div className="flex gap-1 mb-2">
                      {[...Array(review.rating)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 fill-amber text-amber" />
                      ))}
                    </div>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      "{review.text}"
                    </p>
                  </div>
                </div>
                <div className="flex items-center justify-between pt-4 border-t border-white/10">
                  <div>
                    <p className="font-bold">{review.name}</p>
                    <p className="text-xs text-muted-foreground">{review.business}</p>
                  </div>
                  {review.verified && (
                    <div className="flex items-center gap-1 text-xs text-neon-green">
                      <CheckCircle className="w-3 h-3" />
                      {isGerman ? "Verifiziert" : "Verified"}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="text-center glass-morphism border border-neon-purple/30 rounded-2xl p-10 max-w-3xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-bold mb-4">
              {isGerman
                ? "Bereit für Ihre eigene Erfolgsgeschichte?"
                : "Ready for Your Own Success Story?"}
            </h2>
            <p className="text-muted-foreground mb-8">
              {isGerman
                ? "Starten Sie noch heute und erhalten Sie Ihre AI UGC Videos in 72 Stunden."
                : "Get started today and receive your AI UGC videos in 72 hours."}
            </p>
            <Button
              asChild
              size="lg"
              className="bg-gradient-to-r from-neon-purple via-neon-pink to-orange-500 text-white font-bold px-12"
            >
              <Link to="/order">
                {isGerman ? "Jetzt starten" : "Get Started"}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </Button>
          </div>

          {/* Internal Links */}
          <div className="mt-20 pt-10 border-t border-white/10 text-center">
            <div className="flex flex-wrap justify-center gap-4 text-sm">
              <Link to="/faq" className="text-neon-purple hover:underline">FAQ</Link>
              <Link to="/pricing" className="text-neon-purple hover:underline">
                {isGerman ? "Preise" : "Pricing"}
              </Link>
              <Link to="/blog/fuel72-erfahrungen" className="text-neon-purple hover:underline">
                {isGerman ? "Mehr Erfahrungen" : "More Reviews"}
              </Link>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default Reviews;